import * as tslib_1 from "tslib";
import setShowListPane from '../actions/setShowListPane';
import setShowReadingPane from '../actions/setShowReadingPane';
import { is3SFeatureEnabled } from 'owa-mail-feature-flags';
import { MailRowDataPropertyGetter } from 'owa-mail-list-store';
import { isDumpsterTable, listViewStore } from 'owa-mail-list-store';
import getInstrumentationContextsFromTableView from 'owa-mail-list-store/lib/utils/getInstrumentationContextsFromTableView';
import { lazyLoadConversationReadingPaneForSingleMailItemSelected, lazyLoadItemReadingPaneForSingleMailItemSelected, } from 'owa-mail-reading-pane-store';
import { isDumpsterSearchTable } from 'owa-mail-search-store';
import { lazyLogEntityClicked } from 'owa-search-service';
import MailListItemSelectionSource from 'owa-mail-store/lib/store/schema/MailListItemSelectionSource';
import mailStore from 'owa-mail-store/lib/store/Store';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import ReadingPanePosition from 'owa-session-store/lib/store/schema/ReadingPanePosition';
import { select, action } from 'satcheljs/lib/legacy';
import isValidConversationCache from 'owa-mail-reading-pane-store/lib/utils/isValidConversationCache';
import { loadConversation } from 'owa-mail-store-actions';
/**
 * Handles no item selection in list view
 */
function handleListViewNoItemSelectedChange(globalReadingPanePosition) {
    setShowListPane(true /* showListPane */);
    if (globalReadingPanePosition === ReadingPanePosition.Off) {
        setShowReadingPane(false /* showReadingPane */);
    }
    else {
        setShowReadingPane(true /* showReadingPane */);
    }
}
/**
 * Handles single item selection in list view
 * @param rowId The row id
 * @param listViewType The list view type
 * @param subject The subject
 * @param isUserNavigation Did user initiate selection
 * @param mailListItemSelectionSource The source of selection on mail item
 */
function handleListViewSingleItemSelectedChangedWhenReadingPaneOff(rowId, listViewType, subject, categories, isUserNavigation, mailListItemSelectionSource, instrumentationContext, shouldLoadCompose) {
    var promiseToReturn = Promise.resolve();
    var showReadingPane = false;
    var showListPane = true;
    // When RP is off, we should only show RP if the user
    // 1) clicks the mail item or the commandbar arrows
    // 2) presses keyboard shortcut
    switch (mailListItemSelectionSource) {
        case MailListItemSelectionSource.MailListItemBody:
        case MailListItemSelectionSource.CommandBarArrows: // Only shown in immersive reading pane
        case MailListItemSelectionSource.KeyboardEnter:
        case MailListItemSelectionSource.MailListItemExpansion:
        case MailListItemSelectionSource.MailListItemRichPreview:
        case MailListItemSelectionSource.ImmersiveReadingPaneDelete:
        case MailListItemSelectionSource.SearchSuggestionClick:
            showReadingPane = true;
            showListPane = false;
            break;
        case MailListItemSelectionSource.MailListItemTwisty:
            promiseToReturn = loadConversationItemsFromTwistyClick(rowId);
            showReadingPane = false;
            showListPane = true;
            break;
        case MailListItemSelectionSource.KeyboardUpDown:
        case MailListItemSelectionSource.MailListItemCheckbox:
        case MailListItemSelectionSource.MailListItemContextMenu:
        case MailListItemSelectionSource.NewConversation:
        case MailListItemSelectionSource.Reset:
        case MailListItemSelectionSource.RouteHandler:
        case MailListItemSelectionSource.RowRemoval:
        default:
            showReadingPane = false;
            showListPane = true;
            break;
    }
    if (showReadingPane) {
        switch (listViewType) {
            case 0 /* Conversation */:
                promiseToReturn = lazyLoadConversationReadingPaneForSingleMailItemSelected.importAndExecute(rowId, isUserNavigation, instrumentationContext, subject, categories);
                break;
            case 1 /* Message */:
                promiseToReturn = lazyLoadItemReadingPaneForSingleMailItemSelected.importAndExecute(rowId, isUserNavigation, instrumentationContext, shouldLoadCompose);
                break;
        }
    }
    setShowReadingPane(showReadingPane);
    setShowListPane(showListPane);
    return promiseToReturn;
}
/**
 * Handles single item selection in list view
 * @param rowId The row id
 * @param listViewType the list view type
 * @param subject The subject
 * @param isUserNavigation Did user initiate selection
 * @param mailListItemSelectionSource The source of selection on mail item
 */
function handleListViewSingleItemSelectedChangedWhenReadingPaneOn(rowId, listViewType, subject, categories, isUserNavigation, mailListItemSelectionSource, instrumentationContext, shouldLoadCompose) {
    setShowReadingPane(true /* showReadingPane */);
    setShowListPane(true /* showListPane */);
    switch (listViewType) {
        case 0 /* Conversation */:
            return lazyLoadConversationReadingPaneForSingleMailItemSelected.importAndExecute(rowId, isUserNavigation, instrumentationContext, subject, categories);
        case 1 /* Message */:
            return lazyLoadItemReadingPaneForSingleMailItemSelected.importAndExecute(rowId, isUserNavigation, instrumentationContext, shouldLoadCompose);
        default:
            return Promise.resolve();
    }
}
/**
 * Called when selection in list view changes to orchestrate what happens next
 * @param tableView The tableview
 * @param isUserNavigation Did user initiate selection
 * @param mailListItemSelectionSource The source of selection on mail item
 * Note that it needs to be an action to make all operations inside atomic.
 */
var onListViewSelectionChange = action('onListViewSelectionChange')(select({
    listViewStore: function () { return listViewStore; },
})(function onListViewSelectionChange(tableView, isUserNavigation, mailListItemSelectionSource, state) {
    return tslib_1.__awaiter(this, void 0, void 0, function () {
        var promiseToReturn, selectedRowsCount, globalReadingPanePosition, rowKey, rowId, isDraft, isSubmitted, tableQuery, shouldAlwaysShowMultiselectRP, shouldLoadCompose, instrumentationContext, categories, subject;
        return tslib_1.__generator(this, function (_a) {
            if (mailListItemSelectionSource == MailListItemSelectionSource.MessageAd) {
                // return when MailListItemSelectionSource is MessageAd as selectedRowsCount is 0 at this time
                // We should not enter the handleListViewNoItemSelectedChange block to change the reading pane hide/show status
                return [2 /*return*/];
            }
            promiseToReturn = Promise.resolve();
            selectedRowsCount = tableView.selectedRowKeys.size;
            globalReadingPanePosition = getUserConfiguration().UserOptions
                .GlobalReadingPanePositionReact;
            if (selectedRowsCount == 0) {
                handleListViewNoItemSelectedChange(globalReadingPanePosition);
            }
            else if (selectedRowsCount == 1) {
                rowKey = tableView.selectedRowKeys.keys()[0];
                rowId = MailRowDataPropertyGetter.getRowClientItemId(rowKey, tableView);
                isDraft = MailRowDataPropertyGetter.getIsDraft(rowKey, tableView);
                isSubmitted = MailRowDataPropertyGetter.getIsSubmitted(rowKey, tableView);
                tableQuery = tableView.tableQuery;
                shouldAlwaysShowMultiselectRP = isDumpsterTable(tableQuery) || isDumpsterSearchTable(tableQuery);
                shouldLoadCompose = isDraft && !isSubmitted && !shouldAlwaysShowMultiselectRP;
                instrumentationContext = getInstrumentationContextsFromTableView([rowKey], tableView)[0];
                categories = MailRowDataPropertyGetter.getCategories(rowKey, tableView);
                subject = tableQuery.listViewType == 0 /* Conversation */
                    ? state.listViewStore.conversationItems.get(rowId.Id).subject
                    : mailStore.items.get(rowId.Id).Subject;
                if (is3SFeatureEnabled('sea-qf') && instrumentationContext) {
                    lazyLogEntityClicked.importAndExecute(instrumentationContext.traceId, instrumentationContext.referenceKey, new Date(), instrumentationContext.index);
                }
                if (!shouldAlwaysShowMultiselectRP) {
                    if (globalReadingPanePosition == ReadingPanePosition.Right ||
                        globalReadingPanePosition == ReadingPanePosition.Bottom) {
                        promiseToReturn = handleListViewSingleItemSelectedChangedWhenReadingPaneOn(rowId, tableQuery.listViewType, subject, categories, isUserNavigation, mailListItemSelectionSource, instrumentationContext, shouldLoadCompose);
                    }
                    else if (globalReadingPanePosition == ReadingPanePosition.Off) {
                        promiseToReturn = handleListViewSingleItemSelectedChangedWhenReadingPaneOff(rowId, tableQuery.listViewType, subject, categories, isUserNavigation, mailListItemSelectionSource, instrumentationContext, shouldLoadCompose);
                    }
                }
            }
            else {
                // Multi-select case
                // VSO 11922: MailModule: Move reading pane states into Mail Module from Reading Pane.
                if (globalReadingPanePosition == ReadingPanePosition.Off) {
                    setShowReadingPane(false /* showReadingPane */);
                    setShowListPane(true /* showListPane */);
                }
            }
            return [2 /*return*/, promiseToReturn];
        });
    });
}));
function loadConversationItemsFromTwistyClick(rowId) {
    // If has valid conversation cache, no need to load item parts
    if (isValidConversationCache(rowId.Id)) {
        return Promise.resolve();
    }
    return loadConversation(rowId, 'LoadConversationReadingPane');
}
export default onListViewSelectionChange;
//# sourceMappingURL=ListViewSelectionChangeOrchestrator.js.map